import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTenant, AdminApiError } from '../../../shared/services/adminApi';

function StatusBadge({ status }) {
  const styles = {
    active: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    trial: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
    suspended: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${styles[status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function RoleBadge({ role }) {
  const styles = {
    admin: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    estimator: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    viewer: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${styles[role] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
}

export function TenantDetail() {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    async function fetchTenant() {
      try {
        setLoading(true);
        setError(null);
        const data = await getTenant(id);
        // Ensure required nested objects exist with defaults
        setTenant({
          ...data,
          settings: data.settings || {
            defaultMarkupPercent: 15,
            defaultProfitMargin: 10,
            defaultOverheadPercent: 12,
            currency: 'USD',
            timezone: 'America/New_York',
          },
          branding: data.branding || {
            logoUrl: null,
            faviconUrl: null,
            loginBackgroundUrl: null,
            colors: {
              primary: '#2563eb',
              secondary: '#64748b',
              accent: '#10b981',
            },
          },
          stats: data.stats || {
            jobCount: 0,
            quoteCount: 0,
            userCount: 0,
          },
          users: data.users || [],
        });
      } catch (err) {
        console.error('Failed to load tenant:', err);
        if (err instanceof AdminApiError) {
          if (err.status === 401) {
            setError('Please log in to view this tenant');
          } else if (err.status === 403) {
            setError('You do not have permission to view this tenant');
          } else if (err.status === 404) {
            setError('Tenant not found');
          } else {
            setError(err.message || 'Failed to load tenant');
          }
        } else {
          setError('Failed to load tenant. Please check your connection.');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchTenant();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
        {error || 'Tenant not found'}
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'branding', label: 'Branding' },
    { id: 'users', label: 'Users' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tenant.companyName}</h1>
            <StatusBadge status={tenant.status} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
            <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">{tenant.subdomain}.quotethisjob.com</code>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/admin/tenants/${tenant.tenantId}/upload`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload CSV
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Historical Jobs</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tenant.stats.jobCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Quotes Created</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tenant.stats.quoteCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Team Members</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tenant.stats.userCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Plan</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{tenant.tier.toUpperCase()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Tenant Information</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Tenant ID</dt>
                  <dd className="text-sm font-mono text-gray-900 dark:text-gray-100">{tenant.tenantId}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Industry</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100">{tenant.industry}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100">{new Date(tenant.createdAt).toLocaleDateString()}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Last Updated</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100">{new Date(tenant.updatedAt).toLocaleDateString()}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Branding Configuration</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure the client's branded login page and dashboard appearance.
            </p>

            {/* Color Preview */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Brand Colors</h4>
              <div className="flex items-center gap-4">
                <div>
                  <div
                    className="w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-800"
                    style={{ backgroundColor: tenant.branding.colors.primary }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Primary</p>
                </div>
                <div>
                  <div
                    className="w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-800"
                    style={{ backgroundColor: tenant.branding.colors.secondary }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Secondary</p>
                </div>
                <div>
                  <div
                    className="w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-800"
                    style={{ backgroundColor: tenant.branding.colors.accent }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Accent</p>
                </div>
              </div>
            </div>

            {/* Logo & Assets */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Brand Assets</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 text-center">
                  <svg className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Logo</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{tenant.branding.logoUrl || 'Not uploaded'}</p>
                </div>
                <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 text-center">
                  <svg className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Favicon</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{tenant.branding.faviconUrl || 'Not uploaded'}</p>
                </div>
                <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 text-center">
                  <svg className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Login Background</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{tenant.branding.loginBackgroundUrl || 'Not uploaded'}</p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">
                Edit Branding
              </button>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Team Members</h3>
              <button className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">
                Add User
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {tenant.users.map((user) => (
                    <tr key={user.userId}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{user.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
                      <td className="px-4 py-3">
                        <RoleBadge role={user.role} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 text-sm">Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Tenant Settings</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Default Markup %
                </label>
                <input
                  type="number"
                  value={tenant.settings.defaultMarkupPercent}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Default Profit Margin %
                </label>
                <input
                  type="number"
                  value={tenant.settings.defaultProfitMargin}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Default Overhead %
                </label>
                <input
                  type="number"
                  value={tenant.settings.defaultOverheadPercent}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Timezone
                </label>
                <input
                  type="text"
                  value={tenant.settings.timezone}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  readOnly
                />
              </div>
            </div>

            <div className="pt-4">
              <button className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">
                Edit Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TenantDetail;
